import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStyles from "../styles";
import { Add } from "@mui/icons-material";
import GraphOptionsButtons from "../graphIconsButtons";
import MetricsCard from "../metricsCard";
import ColorCustomization from "../colorCustomization";
import DatagridColumnItem from "../datagridColumns";
import HtmlEditor from "../htmlEditor";
import { useFilters } from "../../../context/filterContext";

interface GraphicsCardProps {
  data: any;
  id: string;
  isSplit: boolean;
  handleUpdateGraphic: (newGraphic: DashGraphicsCards) => void;
}

interface Metric {
  key: string;
  type: string;
  label: string;
}

export interface DatagridColumn {
  headerName: string;
  field: string;
  flex?: number;
  minWidth?: number;
  width?: number;
  myColumnType: string;
  colorsByKey?: Record<
    string,
    {
      text: string;
      color: string;
    }
  >;
  [key: string]: any;
}

const GraphicsCard = ({
  data,
  isSplit,
  handleUpdateGraphic,
  id,
}: GraphicsCardProps) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [graphicData, setGraphicData] = useState<DashGraphicsCards>(data);
  const [metricsSwitch, setMetricsSwitch] = useState<boolean>(false);
  const [colorsBykeySwitch, setColorsBykeySwitch] = useState<boolean>(false);
  const [openColorOptionIndex, setOpenColorOptionIndex] = useState<
    number | null
  >(null);
  const { filtersConfig } = useFilters();
  useEffect(() => {
    if (!data) return;
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (JSON.stringify(prev) !== JSON.stringify(data)) {
        return data;
      }
      return prev;
    });
    if (Array.isArray(data?.metricsType) && data?.metricsType.length > 0) {
      setMetricsSwitch(true);
    }
    if (
      data.config.colorsByKey &&
      Object.keys(data?.config?.colorsByKey).length > 0
    ) {
      setColorsBykeySwitch(true);
    }
  }, []);

  useEffect(() => {
    if (!isSplit && graphicData) {
      const fieldsToUpdate = ["title", "subtitle", "description"];
      fieldsToUpdate.forEach((field) => {
        if (graphicData[field as keyof DashGraphicsCards]) {
          handleChangeGraph(field, "");
        }
      });
    }
  }, [isSplit]);

  const handleAddColumn = () => {
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (!prev) return prev;
      const newColumn = {
        field: "",
        width: 100,
        headerName: "",
        myColumnType: "text",
      };

      const updatedGraphic = {
        ...data,
        config: {
          ...data.config,
          columns: [...(data.config.columns || []), newColumn],
        },
      };

      handleUpdateGraphic(updatedGraphic as DashGraphicsCards);

      return updatedGraphic;
    });
  };

  const handleColorsByKeySwitch = () => {
    setColorsBykeySwitch((prev) => {
      const newSwitchState = !prev;

      setGraphicData((prevGraphicData: DashGraphicsCards | undefined) => {
        if (!prevGraphicData) return prevGraphicData;
        let updatedGraphic: DashGraphicsCards;
        if (newSwitchState) {
          updatedGraphic = {
            ...prevGraphicData,
            config: {
              ...prevGraphicData.config,
              colorsByKey: prevGraphicData.config.colorsByKey || {},
            },
          };
        } else {
          const { colorsByKey, ...configWithoutColorsByKey } =
            prevGraphicData.config;
          updatedGraphic = {
            ...prevGraphicData,
            config: configWithoutColorsByKey,
          };
        }
        handleUpdateGraphic(updatedGraphic);
        return updatedGraphic;
      });

      return newSwitchState;
    });
  };

  const handleMetricsSwitch = () => {
    setMetricsSwitch((prev) => {
      const newSwitchState = !prev;

      setGraphicData((prevGraphicData: DashGraphicsCards | undefined) => {
        if (!prevGraphicData) return prevGraphicData;
        let updatedGraphic: DashGraphicsCards;
        if (newSwitchState) {
          updatedGraphic = {
            ...prevGraphicData,
            metricsType: prevGraphicData.metricsType || [],
          };
        } else {
          updatedGraphic = {
            ...prevGraphicData,
            metricsType: [],
          };
        }
        handleUpdateGraphic(updatedGraphic);
        return updatedGraphic;
      });

      return newSwitchState;
    });
  };

  const handleUpdateColumns = (
    newColumns: DatagridColumn,
    columnIndex: number,
  ) => {
    const columnsUpdated = data.config.columns.map(
      (column: DatagridColumn, index: number) =>
        index === columnIndex ? { ...newColumns } : column,
    );

    const newGraphic = {
      ...data,
      config: {
        ...data.config,
        columns: columnsUpdated,
      },
    };

    handleUpdateGraphic(newGraphic as DashGraphicsCards);
  };

  const handleColumnDelete = (index: number) => {
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (!prev) return prev;
      const updatedColumns: DatagridColumn[] = (
        data.config.columns || []
      ).filter((_: DatagridColumn, i: number) => i !== index);

      const updatedGraphic = {
        ...data,
        config: {
          ...data.config,
          columns: updatedColumns,
        },
      };
      handleUpdateGraphic(updatedGraphic as DashGraphicsCards);

      return updatedGraphic;
    });
  };

  const getColorsSchema = useMemo(() => {
    const colorsSchema: Record<string, any[]> = {};
    for (const key in theme.palette.cromatic) {
      colorsSchema[key] = Object.values(
        theme.palette.cromatic[key as keyof typeof theme.palette.cromatic],
      );
    }
    return colorsSchema;
  }, [theme.palette.cromatic]);

  const getAllColors = useMemo(() => {
    const result: Record<string, { [key: string]: string | boolean }[]> = {};
    for (const key in theme.palette.cromatic) {
      const colorShades = theme.palette.cromatic[
        key as keyof typeof theme.palette.cromatic
      ] as Record<string, string>;
      result[key] = Object.entries(colorShades).map(([shade, value]) => {
        const colorKey = `${key}.${shade}`;
        const colorObj: { [key: string]: string | boolean } = {
          [colorKey]: value,
        };
        if (shade === "main") {
          colorObj.main = true;
        }
        return colorObj;
      });
    }
    return result;
  }, [theme.palette.cromatic]);

  const getColorFromPallet = useCallback(
    (index: number): string => {
      const mainColorNames = Object.entries(getAllColors).map(
        ([colorKey, colorList]) => {
          const mainColorObj = colorList.find((colorObj) => colorObj.main);
          if (mainColorObj) {
            return Object.keys(mainColorObj).find(
              (k) => k !== "main",
            ) as string;
          }
          return "purple.main";
        },
      );
      const normalizedIndex = index % mainColorNames.length;
      return mainColorNames[normalizedIndex] || "purple.main";
    },
    [getAllColors],
  );

  const handleChangeGraph = useCallback(
    (key: string, value: string) => {
      setGraphicData((prev: DashGraphicsCards | undefined) => {
        if (!prev) return prev;
        const updatedGraphic = {
          ...prev,
          [key]: value,
          config:
            key === "type" && value === "pie"
              ? { ...prev.config, subType: "donut" }
              : { ...prev.config },
        };

        handleUpdateGraphic(updatedGraphic);
        return updatedGraphic;
      });
    },
    [handleUpdateGraphic],
  );

  const changeGraphConfig = useCallback(
    (key: string, value: any) => {
      setGraphicData((prev: DashGraphicsCards | undefined) => {
        if (!prev) return prev;
        const updatedGraphic = {
          ...prev,
          config: {
            ...prev.config,
            [key]: value,
          },
        };
        handleUpdateGraphic(updatedGraphic);
        return updatedGraphic;
      });
    },
    [handleUpdateGraphic],
  );

  const addGraphDataConfigColors = (key: string, value: string) => {
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (!prev) return prev;
      const updatedGraphic = {
        ...prev,
        config: {
          ...prev.config,
          colorsByKey: {
            ...prev.config.colorsByKey,
            [key]: value,
          },
        },
      };
      handleUpdateGraphic(updatedGraphic);
      return updatedGraphic;
    });
  };

  const handleAddNewColorKey = () => {
    const existingKeys = Object.keys(graphicData?.config?.colorsByKey || {});
    let newKey = "1";
    for (let i = 1; ; i++) {
      if (!existingKeys.includes(String(i))) {
        newKey = String(i);
        break;
      }
    }
    const newColor = getColorFromPallet(existingKeys.length);
    addGraphDataConfigColors(newKey, newColor);
  };

  const handleRemoveColorKey = (key: string) => {
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (!prev) return prev;
      const updatedColorsByKey = { ...prev.config.colorsByKey };
      delete updatedColorsByKey[key];
      const updatedGraphic = {
        ...prev,
        config: {
          ...prev.config,
          colorsByKey: updatedColorsByKey,
        },
      };
      handleUpdateGraphic(updatedGraphic);
      return updatedGraphic;
    });
  };

  const handleAddMetrics = () => {
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (!prev) return prev;
      const updatedGraphic = {
        ...prev,
        metricsType: [
          ...(prev.metricsType || []),
          { key: "", type: "avg", label: "" },
        ],
      };
      handleUpdateGraphic(updatedGraphic);
      return updatedGraphic;
    });
  };
  const handleMetricChange = useCallback(
    (property: keyof Metric, value: string, metricIndex: number) => {
      setGraphicData((prev: DashGraphicsCards | undefined) => {
        if (!prev) return prev;
        const updatedMetrics: Metric[] = (prev.metricsType || []).map(
          (metric: Metric, index: number) =>
            index === metricIndex ? { ...metric, [property]: value } : metric,
        );
        const updatedGraphic = {
          ...prev,
          metricsType: updatedMetrics,
        };
        handleUpdateGraphic(updatedGraphic);
        return updatedGraphic;
      });
    },
    [handleUpdateGraphic],
  );

  const handleDeleteMetrics = (_e: React.MouseEvent, metricIndex: number) => {
    setGraphicData((prev: DashGraphicsCards | undefined) => {
      if (!prev) return prev;
      const updatedMetrics: Metric[] = (prev.metricsType || []).filter(
        (_: Metric, index: number) => index !== metricIndex,
      );
      const updatedGraphic = {
        ...prev,
        metricsType: updatedMetrics,
      };
      handleUpdateGraphic(updatedGraphic);
      return updatedGraphic;
    });
  };

  const handleColMoveUp = (index: number) => {
    const columns = graphicData?.config.columns || [];
    if (index === 0) return;
    const newColumns = [...columns];
    const [element] = newColumns.splice(index, 1);
    newColumns.splice(index - 1, 0, element);

    const newGraphic = {
      ...graphicData,
      config: {
        ...graphicData.config,
        columns: newColumns,
      },
    };
    setGraphicData(newGraphic);
    handleUpdateGraphic(newGraphic);
  };

  const handleColMoveDown = (index: number) => {
    const columns = graphicData?.config.columns || [];
    if (index === columns.length - 1) return;
    const newColumns = [...columns];
    const [element] = newColumns.splice(index, 1);
    newColumns.splice(index + 1, 0, element);

    const newGraphic = {
      ...graphicData,
      config: {
        ...graphicData.config,
        columns: newColumns,
      },
    };
    setGraphicData(newGraphic);
    handleUpdateGraphic(newGraphic);
  };

  useEffect(() => {
    if (graphicData.type === "repeat" || graphicData.type === "datagrid")
      return;

    const isAxisChart = ["line", "bar", "lineBar"].includes(graphicData.type);

    if (isAxisChart) {
      const defaults = {
        units: graphicData?.config.units || " ",
        groupBy: graphicData?.config.groupBy || "date",
        groupMethod: graphicData?.config.groupMethod || "sum",
        groupMode: graphicData?.config.groupMode || "stacked",
        interval: graphicData?.config.interval || "month",
        viewMode: graphicData?.config.viewMode || "linear",
        curve: graphicData?.config.curve || "basis",
      };

      Object.entries(defaults).forEach(([key, value]) => {
        if (graphicData?.config[key] !== value) {
          changeGraphConfig(key, value);
        }
      });
    } else {
      const fieldsToClear = [
        "groupBy",
        "groupMethod",
        "groupMode",
        "interval",
        "viewMode",
        "curve",
      ];

      fieldsToClear.forEach((field) => {
        if (graphicData?.config[field]) {
          changeGraphConfig(field, undefined);
        }
      });
    }
  }, [graphicData.type]);

  return (
    <Box sx={styles.paper} key={`coluna-${id}`}>
      {isSplit && graphicData?.type !== "cards" && (
        <>
          <TextField
            label="Título"
            variant="filled"
            value={graphicData?.title || ""}
            onChange={(e) => handleChangeGraph("title", e.target.value)}
          />
          <TextField
            label="Subtítulo"
            variant="filled"
            value={graphicData?.subtitle || ""}
            onChange={(e) => handleChangeGraph("subtitle", e.target.value)}
          />
          <TextField
            label="Descrição"
            variant="filled"
            multiline
            rows={3}
            value={graphicData?.description || ""}
            onChange={(e) => handleChangeGraph("description", e.target.value)}
          />
        </>
      )}

      <Typography sx={{ marginTop: 2, marginBottom: 2 }}>
        Selecione o tipo de gráfico
      </Typography>
      <Box height={"100%"}>
        <GraphOptionsButtons
          graphType={graphicData?.type}
          setGraphType={(value: string) => handleChangeGraph("type", value)}
        />
      </Box>
      {graphicData?.type !== "datagrid" && (
        <>
          <Divider />
          {(graphicData?.type === "bar" ||
            graphicData?.type === "line" ||
            graphicData?.type === "lineBar") && (
            <Stack sx={styles.stackSwitch}>
              <FormControl sx={styles.switchFormControl}>
                <FormLabel
                  sx={{
                    color: theme.palette.text.primary,
                    "&.Mui-focused": {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  Transformar Chave
                </FormLabel>
                <FormControlLabel
                  value="bottom"
                  control={
                    <Switch
                      sx={{
                        marginLeft: -1,
                      }}
                      checked={graphicData?.config?.parseKeyToColumn}
                      onChange={(e) =>
                        changeGraphConfig("parseKeyToColumn", e.target.checked)
                      }
                    />
                  }
                  label="Transforma primeira chave em coluna"
                  labelPlacement="top"
                  sx={styles.switchLabel}
                />
              </FormControl>
              {(graphicData?.type === "bar" ||
                graphicData?.type === "lineBar") && (
                <FormControl sx={styles.switchFormControl}>
                  <FormLabel
                    sx={{
                      color: theme.palette.text.primary,
                      "&.Mui-focused": {
                        color: theme.palette.text.primary,
                      },
                    }}
                  >
                    Exibir total
                  </FormLabel>
                  <FormControlLabel
                    value="bottom"
                    control={
                      <Switch
                        sx={{
                          marginLeft: -1,
                        }}
                        checked={graphicData?.config?.enableTotals}
                        onChange={(e) =>
                          changeGraphConfig("enableTotals", e.target.checked)
                        }
                      />
                    }
                    label="Exibir total acima das barras"
                    labelPlacement="top"
                    sx={styles.switchLabel}
                  />
                </FormControl>
              )}
            </Stack>
          )}
          {graphicData?.type !== "repeat" && (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={graphicData?.config?.keys || []}
              onChange={(_, newValue) => changeGraphConfig("keys", newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Chaves" variant="filled" />
              )}
              // onBlur={(event) => {
              //   const inputValue = (
              //     event.target as HTMLInputElement
              //   ).value.trim();
              //   if (inputValue) {
              //     changeGraphConfig("keys", [
              //       ...(graphicData?.config?.keys || []),
              //       inputValue,
              //     ]);
              //   }
              // }}
            />
          )}
          {graphicData?.type == "repeat" && (
            <Autocomplete
              id="multiple-limit-tags"
              value={
                filtersConfig?.find(
                  (f) => f.variable === graphicData?.config?.repeatBy,
                ) || null
              }
              //value={graphicData?.config?.repeatBy || ""}
              onChange={(e, value: Filters | null) =>
                changeGraphConfig("repeatBy", value?.variable)
              }
              options={filtersConfig || []}
              getOptionLabel={(option: Filters) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Repetir por"
                  variant="filled"
                  //  sx={styles.textfield}
                />
              )}
            />
          )}
          {graphicData?.type === "lineBar" && (
            <TextField
              label="Chave da linha"
              variant="filled"
              value={graphicData?.config.keyLine}
              onChange={(e) => changeGraphConfig("keyLine", e.target.value)}
            />
          )}
          {graphicData?.type !== "cards" && graphicData?.type !== "repeat" && (
            <TextField
              label="Indexar por"
              variant="filled"
              value={graphicData?.config.indexBy}
              onChange={(e) => changeGraphConfig("indexBy", e.target.value)}
            />
          )}
          {(graphicData?.type === "bar" ||
            graphicData?.type === "line" ||
            graphicData?.type === "lineBar") && (
            <TextField
              select
              label="Agrupar por"
              variant="filled"
              value={graphicData?.config.groupBy || "date"}
              onChange={(e) => changeGraphConfig("groupBy", e.target.value)}
            >
              <MenuItem value={"date"}>Data</MenuItem>
              <MenuItem value={"category"}>Categoria</MenuItem>
            </TextField>
          )}

          {(graphicData?.type === "bar" || graphicData?.type === "lineBar") && (
            <TextField
              select
              label="Método de grupo"
              variant="filled"
              value={graphicData?.config.groupMethod || "sum"}
              onChange={(e) => changeGraphConfig("groupMethod", e.target.value)}
            >
              <MenuItem value={"sum"}>Soma</MenuItem>
              <MenuItem value={"average"}>Média</MenuItem>
            </TextField>
          )}

          {(graphicData?.type === "circular-progress" ||
            graphicData?.type === "linear") && (
            <TextField
              select
              label="Orientação"
              variant="filled"
              value={graphicData?.config.orientation || "horizontal"}
              onChange={(e) => changeGraphConfig("orientation", e.target.value)}
            >
              <MenuItem value={"horizontal"}>Horizontal</MenuItem>
              <MenuItem value={"vertical"}>Vertical</MenuItem>
            </TextField>
          )}
          {graphicData?.type === "sankey" && (
            <TextField
              label="Indexar código por"
              variant="filled"
              value={graphicData?.config.codeIndexBy}
              onChange={(e) => changeGraphConfig("codeIndexBy", e.target.value)}
            />
          )}
          {graphicData?.type !== "heatmap" &&
            graphicData?.type !== "repeat" && (
              <>
                <TextField
                  select
                  label="Unidade"
                  variant="filled"
                  value={graphicData?.config.units || " "}
                  onChange={(e) => changeGraphConfig("units", e.target.value)}
                >
                  <MenuItem value={" "}>Numerico</MenuItem>
                  <MenuItem value={"R$"}>R$</MenuItem>
                  <MenuItem value={"%"}>%</MenuItem>
                </TextField>
                <TextField
                  label="Quantidade de casas decimais"
                  variant="filled"
                  type="text"
                  value={graphicData?.config.fractionDigits}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      /^(0|[1-9]\d*)?$/.test(value) &&
                      (value === "" || Number(value) <= 100)
                    ) {
                      changeGraphConfig("fractionDigits", value);
                    }
                  }}
                />
              </>
            )}
          {(graphicData?.type === "bar" || graphicData?.type === "lineBar") && (
            <TextField
              select
              label="Modo de agrupamento"
              variant="filled"
              value={graphicData?.config.groupMode || "stacked"}
              onChange={(e) => changeGraphConfig("groupMode", e.target.value)}
            >
              <MenuItem value={"grouped"}>Agrupado</MenuItem>
              <MenuItem value={"stacked"}>Empilhado</MenuItem>
            </TextField>
          )}
          {graphicData?.type === "line" && (
            <TextField
              select
              label="Interpolação da curva"
              variant="filled"
              value={graphicData?.config.curve || "basis"}
              onChange={(e) => changeGraphConfig("curve", e.target.value)}
            >
              <MenuItem value={"basis"}>Basis</MenuItem>
              <MenuItem value={"cardinal"}>Cardinal</MenuItem>
              <MenuItem value={"catmullRom"}>CatmullRom</MenuItem>
              <MenuItem value={"linear"}>Linear</MenuItem>
              <MenuItem value={"natural"}>Natural</MenuItem>
              <MenuItem value={"Step"}>Step</MenuItem>
            </TextField>
          )}
          {(graphicData?.type === "bar" ||
            graphicData?.type === "line" ||
            graphicData?.type === "lineBar") && (
            <>
              <TextField
                select
                label="Intervalo inicial"
                variant="filled"
                value={graphicData?.config.interval || "month"}
                onChange={(e) => changeGraphConfig("interval", e.target.value)}
              >
                <MenuItem value={"month"}>Mensal</MenuItem>
                <MenuItem value={"week"}>Semanal</MenuItem>
                <MenuItem value={"day"}>Diário</MenuItem>
              </TextField>
              <TextField
                select
                label="Modo de exibição inicial"
                variant="filled"
                value={graphicData?.config.viewMode || "linear"}
                onChange={(e) => changeGraphConfig("viewMode", e.target.value)}
              >
                <MenuItem value={"accumulate"}>Acumulado</MenuItem>
                <MenuItem value={"linear"}>Linear</MenuItem>
              </TextField>
            </>
          )}
          {graphicData?.type !== "cards" && (
            <>
              {![
                "cards",
                "barHorizontal",
                "linear",
                "circular-progress",
                "linear-progress",
                "datagrid",
                "heatMap",
                "vectorMap",
                "repeat",
              ].includes(graphicData?.type) && (
                <MetricsCard
                  metricsValue={metricsSwitch}
                  setMetricsValue={handleMetricsSwitch}
                  metricsContent={graphicData?.metricsType}
                  handleAddMetrics={handleAddMetrics}
                  handleMetricKeyChange={(e, metricIndex) =>
                    handleMetricChange("key", e.target.value, metricIndex)
                  }
                  handleMetricTypeChange={(e, metricIndex) =>
                    handleMetricChange("type", e.target.value, metricIndex)
                  }
                  handleMetricLabelChange={(e, metricIndex) =>
                    handleMetricChange("label", e.target.value, metricIndex)
                  }
                  handleDeleteMetrics={handleDeleteMetrics}
                />
              )}
              {!["repeat"].includes(graphicData?.type) && (
                <ColorCustomization
                  keyName={Object.keys(graphicData?.config?.colorsByKey || {})}
                  colorValues={Object.values(
                    graphicData?.config?.colorsByKey || {},
                  )}
                  onColorsChange={(keys, values) =>
                    changeGraphConfig(
                      "colorsByKey",
                      keys.reduce(
                        (acc, key, i) => ({ ...acc, [key]: values[i] }),
                        {},
                      ),
                    )
                  }
                  styles={styles}
                  customColorsByKey={colorsBykeySwitch}
                  setCustomColorsByKey={handleColorsByKeySwitch}
                  selectedPalette={graphicData?.config?.colors?.scheme}
                  setSelectedPalette={(value) =>
                    changeGraphConfig("colors", {
                      ...graphicData?.config.colors,
                      scheme: value,
                    })
                  }
                  getColorsSchema={() => getColorsSchema}
                  // colorsByKey={graphicData?.config.colorsByKey}
                  setOpenColorOptionIndex={setOpenColorOptionIndex}
                  openColorOptionIndex={openColorOptionIndex}
                  getAllColors={() => getAllColors}
                  // handleColorPickerSelection={handleColorPickerSelection}
                  getColorFromPallet={getColorFromPallet}
                  handleAddNewColorKey={handleAddNewColorKey}
                  handleRemoveColorKey={handleRemoveColorKey}
                  //handleEditColorKey={() =>handleEditColorKey}
                />
              )}
            </>
          )}
          {graphicData?.type === "cards" && (
            <HtmlEditor
              stringHtml={graphicData?.html}
              defaultCard={graphicData?.defaultCard}
              handleChangeGraph={handleChangeGraph}
            />
          )}
        </>
      )}
      {graphicData?.type === "datagrid" && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography>Colunas</Typography>
            <Button
              sx={{ color: theme.palette.primary.main }}
              startIcon={<Add />}
              onClick={handleAddColumn}
            >
              Adicionar
            </Button>
          </Box>
          {graphicData?.config.columns &&
            graphicData?.config.columns.length > 0 &&
            graphicData?.config.columns.map(
              (column: DatagridColumn, i: number) => (
                <DatagridColumnItem
                  key={`${column.field}-${i}`}
                  column={column}
                  index={i}
                  length={graphicData?.config.columns.length || 0}
                  handleUpdateColumns={handleUpdateColumns}
                  handleColumnDelete={handleColumnDelete}
                  handleColMoveUp={handleColMoveUp}
                  handleColMoveDown={handleColMoveDown}
                  styles={styles}
                  getAllColors={() => getAllColors}
                  getColorFromPallet={getColorFromPallet}
                />
              ),
            )}
        </>
      )}
    </Box>
  );
};

export default React.memo(GraphicsCard);
